import * as z from 'zod';

export const trackingPageTypeSchema = z.union([
  z.literal('product listing page'),
  z.literal('product detail page'),
  z.literal('homepage'),
  z.literal('cart'),
  z.literal('checkout'),
  z.literal('brands'),
  z.literal('unknown')
]);
export type TrackingPageType = z.infer<typeof trackingPageTypeSchema>;

const commonInteractiveEventSchema = z.object({
  event: z.literal('ga4_event'),
  page_type: trackingPageTypeSchema,
  page_url: z.url()
});

const clickPositionSchema = z.union([
  z.literal('header_main_navi'),
  z.literal('burger_menu'),
  z.literal('main_navi'),
  z.literal('footer_navi'),
  z.literal('meta_navi'),
  z.literal('side_navi'),
  z.literal('icon_navi')
]);

export const navigationClickEventSchema = commonInteractiveEventSchema.extend({
  event_name: z.literal('navigation_click'),

  click_name: z.string().nonempty(),
  click_position: clickPositionSchema,
  click_url: z.url().optional()
});
export type NavigationClickEvent = z.infer<typeof navigationClickEventSchema>;

// `filter_action` is a GA4 interaction event emitted for filter-sidebar interactions on
// PLP-like pages (ECOM-5703). It lives in the interactive-event family alongside
// `navigation_click`, but carries its own page-type derivation (`getPageType`) so the
// search-results page and category pages are distinguished — hence its own `page_type`
// schema rather than reusing `trackingPageTypeSchema`.
export const filterActionPageTypeSchema = z.union([
  z.literal('home'),
  z.literal('produktdetail'),
  z.literal('warenkorb'),
  z.literal('checkout'),
  z.literal('danke'),
  z.literal('suche'),
  z.literal('marken'),
  z.literal('kategorie'),
  z.literal('smart-kategorie'),
  z.literal('login'),
  z.literal('kundenkonto'),
  z.literal('wunschliste'),
  z.literal('prospekte'),
  z.literal('storefinder'),
  z.literal('content'),
  z.literal('service'),
  z.literal('not-defined')
]);
export type FilterActionPageType = z.infer<typeof filterActionPageTypeSchema>;

export const filterElementSchema = z.union([
  z.literal('toggle'),
  z.literal('checkbox'),
  z.literal('slider'),
  z.literal('suche'),
  z.literal('link')
]);
export type FilterElement = z.infer<typeof filterElementSchema>;

export const filterInteractionSchema = z.union([
  z.literal('open'),
  z.literal('close'),
  z.literal('apply'),
  z.literal('remove')
]);
export type FilterInteraction = z.infer<typeof filterInteractionSchema>;

export const filterActionEventSchema = z.object({
  event: z.literal('ga4_event'),
  event_name: z.literal('filter_action'),
  page_type: filterActionPageTypeSchema,
  page_url: z.url(),

  filter_category: z.string(),
  filter_element: filterElementSchema,
  filter_interaction: filterInteractionSchema,
  filter_value: z.union([z.string(), z.boolean()]),
  click_url: z.string().optional()
});
export type FilterActionEvent = z.infer<typeof filterActionEventSchema>;
